import React, { useEffect, useState } from 'react';
import { Button } from '@material-ui/core';
import ArrowBack from '@material-ui/icons/ArrowBack';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import './DashboardIRDView.scss';
import {
  dashboardIrdListSelector,
  fetchAllDashboardIrdList,
  setDashboardNewOrder,
} from '../../DashboardSlice';
import LinearProgressBar from '../../../../shared/progress-bar/Progress';
import { notificationManager } from '../../../../utils/NotificationUtils';
import ActivationPopupModel from '../../../../shared/activation-popup/Activation-Popup';
import NotifyPopup from '../../../../shared/notify-popup/Notify-Popup';
import { selectedHotelCurrencySelector } from '../../../property/propertySlice';
import {
  acceptIRDDashboardOrders,
  completeIRDDashboardOrders,
  delayIRDDashboardOrders,
} from '../../../../utils/dashboardAPIUtils';
import { generatedElapsedTime } from '../../../../utils/dashboardUtils';
import { decryptLocalStorage } from '../../../../utils/authUtils';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../utils/loaderSlice';
import {
  RBAC_PERMISSION_LEVEL_FULL_ACCESS,
  RBAC_TYPE_DASHBOARD_IRD,
} from '../../../../constants/RoleConsts';

const DashboardIRDView = (props) => {
  const { obj, onViewToggle, hotelId, showLoader = () => {}, permissions } = props;
  const dispatch = useDispatch();
  const allDashboardIRDList = useSelector(dashboardIrdListSelector);
  const currency = useSelector(selectedHotelCurrencySelector);
  const [order, setOrder] = useState(null);
  const [elapsed, setElapsed] = useState(null);
  const [second, setSecond] = useState(0);
  const [selectedValue, setSelectedValue] = useState({
    id: '',
    name: '',
    title: '',
    description: '',
    status: false,
    type: '',
    descriptionClass: 'delete-popup-description',
  });
  const [orderItems, setOrderItems] = useState([]);

  useEffect(() => {
    const currOrder = allDashboardIRDList.find(
      (dashboardIRDList) => dashboardIRDList.id === obj.id,
    );
    if (currOrder) {
      setOrder(currOrder);
      let generatedItems = currOrder?.items.map((e, i) => {
        return { ...e, isExpand: true, panelId: `panel${i}` };
      });
      setOrderItems(generatedItems);
    }
  }, [obj, allDashboardIRDList]);

  const getTimeDifference = (time, startTime) => {
    const startDate = startTime ? moment.unix(startTime) : moment();
    const endDate = moment.unix(time);
    const timeDifference = generatedElapsedTime(startDate, endDate);
    return timeDifference;
  };

  useEffect(() => {
    if (order) {
      let timeDifference = 0;
      let timer = null;
      if (order.orderStatus !== 'Completed') {
        timer = setTimeout(() => {
          timeDifference = getTimeDifference(order?.createdAt);
          setSecond(second + 1);
          setElapsed(timeDifference);
        }, 1000);
      } else {
        timeDifference = getTimeDifference(order?.completedTime, order?.createdAt);
        setElapsed(timeDifference);
      }
      return () => clearTimeout(timer);
    }
  }, [order, second]);

  const onClickBack = () => {
    dispatch(setDashboardNewOrder(null));
    onViewToggle('', false, null);
  };

  const viewDate = (date) => {
    return moment.unix(date).format('DD MMM, hh:mm A');
  };

  const hasFullAccessPermission =
    permissions && permissions[RBAC_TYPE_DASHBOARD_IRD] === RBAC_PERMISSION_LEVEL_FULL_ACCESS;

  const onClickAcceptOrder = async () => {
    try {
      dispatch(stepUpLoaderCount());
      const lang = decryptLocalStorage('lang');
      const language = lang !== 'en' ? lang : '';
      if (order?.orderStatus !== 'New Order') return false;

      await acceptIRDDashboardOrders(hotelId, obj.id, language);
      await dispatch(fetchAllDashboardIrdList(hotelId));
      notificationManager('success', 'Order accepted successfully');
    } catch (error) {
      notificationManager(
        'danger',
        error?.graphQLErrors
          ? error?.graphQLErrors?.[0]?.message
          : 'Error occurred while accepting order',
      );
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

  const onClickOrderDelayed = () => {
    setSelectedValue({
      id: order.id,
      name: '',
      title: 'Set Order Status to Delay?',
      description:
        'This order will be moved to Delay Status. This action cannot be reversed. You can also choose to notify the guest about the delay.',
      status: true,
      type: 'delayed',
      descriptionClass: 'delete-popup-description-black',
    });
  };

  const handleClose = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      type: '',
      descriptionClass: 'delete-popup-description',
    });
  };

  const onUpdateIRDDashboardOrder = async (reasonText) => {
    const lang = decryptLocalStorage('lang');
    const language = lang !== 'en' ? lang : '';
    await delayIRDDashboardOrders(obj.id, hotelId, reasonText, language);
    await dispatch(fetchAllDashboardIrdList(hotelId));
  };
  const onOkClick = async () => {
    try {
      dispatch(stepUpLoaderCount());
      const lang = decryptLocalStorage('lang');
      const language = lang !== 'en' ? lang : '';

      if (selectedValue.type === 'complete') {
        await completeIRDDashboardOrders(obj.id, hotelId, moment().unix(), language);
        await dispatch(fetchAllDashboardIrdList(hotelId));
        notificationManager('success', 'Order completed successfully');
        handleClose();
      } else {
        await onUpdateIRDDashboardOrder();
        setSelectedValue({
          id: order.id,
          name: '',
          title: `Notify ${order?.guestName} about Order Delay?`,
          description: 'Send a notification about the delay to the guest.',
          status: true,
          type: 'notify',
          descriptionClass: 'delete-popup-description',
        });
      }
    } catch (error) {
      notificationManager(
        'danger',
        error?.graphQLErrors
          ? error?.graphQLErrors?.[0]?.message
          : `Error occurred while ${
              selectedValue.type === 'complete' ? 'complete' : 'delay'
            } order`,
      );
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };
  const onNotify = async (value) => {
    try {
      dispatch(stepUpLoaderCount());
      await onUpdateIRDDashboardOrder(value);
      await dispatch(fetchAllDashboardIrdList(hotelId));
      notificationManager('success', 'Order delayed successfully');
      handleClose();
    } catch (error) {
      notificationManager(
        'danger',
        error?.graphQLErrors ? error?.graphQLErrors?.[0]?.message : 'Error delaying order',
      );
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };
  const onNotNotify = () => {
    handleClose();
  };

  const onClickCompleteOrder = () => {
    if (order?.orderStatus === 'Completed') return false;
    setSelectedValue({
      id: order.id,
      name: '',
      title: 'Move this order to Completed?',
      description: 'This action cannot be reversed.',
      status: true,
      type: 'complete',
      descriptionClass: 'delete-popup-description-black',
    });
  };

  const handleChange = (panel) => (event, newExpanded) => {
    let generatedNewItems = orderItems?.map((item) => {
      return {
        ...item,
        isExpand: item.panelId === panel ? !item.isExpand : item.isExpand,
      };
    });

    setOrderItems(generatedNewItems);
  };

  return (
    <>
      {elapsed === null ? (
        <div className='pt-4 col-md-12'>
          <LinearProgressBar />
        </div>
      ) : (
        <div className='ird-dashboard-view container mt-4 mb-3 detail-view-65 pb-4'>
          <div className='mt-5 row mx-0'>
            <div className='col-md-12 px-0 d-flex align-items-center'>
              <Button
                variant='text'
                color='secondary'
                size='small'
                startIcon={<ArrowBack />}
                onClick={(e) => onClickBack()}
                className='me-2'
              >
                Back
              </Button>
              <span className='page-title'>Room {order?.roomNo}</span>
            </div>
          </div>
          <div className='card shadow mt-4 mb-4 pb-5 h-100 px-4 pt-4'>
            <div className='row mb-4'>
              <div className='col-md-4'>
                <div className='details-label'>Order Id</div>
                <div className='details-value'>{order?.id?.split('-')[0]}</div>
              </div>
              <div className='col-md-4'>
                <div className='details-label'>Date & Time</div>
                <div className='details-value'>{viewDate(order?.createdAt)}</div>
              </div>
              <div className='col-md-4'>
                <div className='details-label'>Delivery Location</div>
                <div className='details-value'>{order?.deliveryLocation || '----'}</div>
              </div>
            </div>
            <div className='row mb-4'>
              <div className='col-md-4'>
                <div className='details-label'>Guest name</div>
                <div className='details-value'>{order?.guestName}</div>
              </div>
              <div className='col-md-4'>
                <div className='details-label'>Status</div>
                <div className={`status ${order?.statusClass}`}>{order?.orderStatus}</div>
              </div>
              <div className='col-md-4'>
                <div className='details-label'>Time Elapsed</div>
                <div className='details-value'>{elapsed}</div>
              </div>
            </div>
            <div className='row mb-4'>
              <div className='col-md-4'>
                <div className='details-label'>No of Items ordered</div>
                <div className='details-value'>{('0' + order?.items?.length).slice(-2)}</div>
              </div>
              <div className='col-md-4'>
                <div className='details-label'>Total Bill Amount</div>
                <div className='details-value'>
                  {currency} {order?.totalAmount}
                </div>
              </div>
              <div className='col-md-4'>
                <div className='details-label'>Payment Method</div>
                <div className='details-value'>{order?.paymentMethod}</div>
              </div>
            </div>
            <div className='row mb-4'>
              <div className='col-md-4'>
                <div className='details-label'>No of guests</div>
                <div className='details-value'>{order?.noOfGuests || 1}</div>
              </div>
              <div className='col-md-4'>
                <div className='details-label'>Channel Type</div>
                <div className='details-value'>{order?.channelType || '---'}</div>
              </div>
            </div>
            <div className='row'>
              <div className='col-md-10'>
                <div className='details-label mb-2'>Items</div>
                {orderItems?.map((currentItem, key) => (
                  <div key={key}>
                    <Accordion
                      className='mb-2'
                      expanded={currentItem.isExpand}
                      onChange={handleChange(`panel${key}`)}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        id='panel1a-header'
                        className='accordion-summary'
                      >
                        <Typography
                          sx={{
                            width: '60%',
                            flexShrink: 0,
                            color: '#707070',
                            fontSize: '16px',
                          }}
                        >
                          {currentItem?.name}
                        </Typography>
                        <Typography
                          sx={{ width: '20%', flexShrink: 0 }}
                          className='accordion-summary-text'
                        >
                          x {currentItem?.count}
                        </Typography>
                        <Typography
                          sx={{ width: '20%', flexShrink: 0 }}
                          className='accordion-summary-text'
                        >
                          {currency} {currentItem?.count * currentItem?.amount}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails className='accordion-details'>
                        <div className='row'>
                          <div className='col-md-2'>
                            {currentItem.image && (
                              <img
                                src={currentItem.image}
                                alt='user'
                                className={currentItem?.name}
                              />
                            )}
                          </div>
                          <div className='col-md-10'>
                            <div className='row'>
                              <div className='col-md-3'>Add Ons:</div>
                              <div className='col-md-9 details-text'>
                                {currentItem.addOns?.map((addon) => addon.name).join(',')}
                              </div>
                            </div>
                            <div className='row'>
                              <div className='col-md-3'>Customisations:</div>
                              <div className='col-md-9 details-text'>
                                {currentItem.customisations
                                  ?.map((customisations) => customisations.name)
                                  .join(',')}
                              </div>
                            </div>
                            <div className='row'>
                              <div className='col-md-3'>Cooking Instructions:</div>
                              <div className='col-md-9 details-text'>
                                {currentItem.cookingInstructions}
                              </div>
                            </div>
                          </div>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  </div>
                ))}
              </div>
            </div>
            <div className='row mt-4'>
              <div className='col-md-10'>
                <div className='details-label'>Additional Note</div>
                <div className='details-additional-note'>{order?.additionalNote || '----'}</div>
              </div>
            </div>
            <div className='row mt-5'>
              <div className='col-md-6'>
                {order?.orderStatus !== 'Completed' && hasFullAccessPermission && (
                  <Button
                    className={order?.orderStatus === 'New Order' ? '' : 'accepted-button-static'}
                    variant={order?.orderStatus === 'New Order' ? 'contained' : 'outlined'}
                    color='primary'
                    type='button'
                    onClick={() => onClickAcceptOrder()}
                  >
                    {order?.orderStatus === 'New Order' ? 'Accept Order' : 'Accepted'}
                  </Button>
                )}
              </div>
              {order?.orderStatus !== 'New Order' && (
                <div className='col-md-4'>
                  <div className='row'>
                    <div className='col-md-6 order-delayed'>
                      {order?.orderStatus !== 'Completed' && (
                        <Button
                          variant='outlined'
                          color='secondary'
                          type='button'
                          className='btn-delayed'
                          onClick={() => onClickOrderDelayed()}
                        >
                          Order Delayed
                        </Button>
                      )}
                    </div>
                    <div className='col-md-6'>
                      <Button
                        className={
                          order?.orderStatus === 'Completed' ? 'completed-button-static' : ''
                        }
                        variant={order?.orderStatus === 'Completed' ? 'outlined' : 'contained'}
                        color='primary'
                        type='button'
                        onClick={() => onClickCompleteOrder()}
                      >
                        {order?.orderStatus === 'Completed' ? 'Order Completed' : 'Complete Order'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {selectedValue.status &&
            (selectedValue.type === 'delayed' || selectedValue.type === 'complete') && (
              <ActivationPopupModel
                activationPopUp={selectedValue}
                cancelButtonName='No'
                okButtonName='Yes'
                onOkClick={onOkClick}
                onActivationPopupActionCancel={handleClose}
              />
            )}
          {selectedValue.status && selectedValue.type === 'notify' && (
            <NotifyPopup
              notifyPopup={selectedValue}
              onNotify={onNotify}
              onNotNotify={onNotNotify}
            />
          )}
        </div>
      )}
    </>
  );
};

export default DashboardIRDView;
